import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer'
import { Product, ProductListRequestFilter, ProductListRequestSorting } from './types'
import { Progress } from 'modules/types'
import { arrToDict, getIds } from 'modules/utils/helpers'
import { ProductState } from './types'
import { LIST_PAGE_SIZE } from 'modules/constants'
import { CalculateDiscountResponse, CartItem } from '../cart/types'
import { FetchError } from 'modules/domain/types'

export const initialState: ProductState = {
  items: {},
  meta: {},
  discountsFetchProgress: Progress.IDLE,
  discountsFetchError: null,
  fetchExclusiveItemProgress: Progress.IDLE,
  fetchExclusiveItemError: null,
  gifts: {},
  giftFilter: {},
  giftListFetchProgress: Progress.IDLE,
  giftListFetchError: null,

  ids: [],
  listFetchProgress: Progress.IDLE,
  listFetchError: null,
  listFetchNextProgress: Progress.IDLE,
  listFetchNextError: null,
  itemFetchProgress: Progress.IDLE,
  itemFetchError: null,
  addProgress: Progress.IDLE,
  addError: null,
  updateProgress: Progress.IDLE,
  updateError: null,
  removeProgress: Progress.IDLE,
  removeError: null,

  filter: {},
  sorting: {},
  page: 1,
  total: 0,
  pageSize: LIST_PAGE_SIZE,

  customProductList: {},
  customProductListFetchProgress: Progress.IDLE,
  customProductListFetchError: null,
}

class ProductReducer extends ImmerReducer<ProductState> {
  listRequested(params: { filter?: ProductListRequestFilter; sorting?: ProductListRequestSorting; page?: number }) {
    this.draftState.listFetchProgress = Progress.WORK
    this.draftState.listFetchError = null
    this.draftState.filter = params.filter || this.draftState.filter
    this.draftState.sorting = params.sorting || this.draftState.sorting
    this.draftState.page = typeof params.page === 'undefined' ? this.draftState.page : params.page
  }
  listRequestSucceed(list: Product[], total: number, page: number) {
    this.draftState.listFetchProgress = Progress.SUCCESS
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map(item => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
    this.draftState.total = total
    this.draftState.page = page
  }
  listRequestFailed(error: FetchError) {
    this.draftState.listFetchProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  itemRequested(idOrSlug: string) {
    this.draftState.itemFetchProgress = Progress.WORK
    const meta = {
      idOrSlug,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }

    this.draftState.meta[idOrSlug] = {
      ...meta,
      ...this.draftState.meta[idOrSlug],
      fetchProgress: Progress.WORK,
      fetchError: null,
    }
  }
  itemRequestSucceed(item: Product) {
    this.draftState.itemFetchProgress = Progress.SUCCESS
    if (!this.draftState.meta[item.id] && this.draftState.meta[item.slug]) {
      this.draftState.meta[item.id] = this.draftState.meta[item.slug]
    }
    this.draftState.meta[item.id].fetchProgress = Progress.SUCCESS
    this.draftState.meta[item.id].fetchError = null
    if (!this.draftState.meta[item.slug]) {
      this.draftState.meta[item.slug] = {
        id: item.id,
        updateProgress: Progress.IDLE,
        updateError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
      }
    } else {
      this.draftState.meta[item.slug].fetchProgress = Progress.SUCCESS
      this.draftState.meta[item.slug].fetchError = null
    }
    this.draftState.items[item.id] = item
    this.draftState.items[item.slug] = item
  }
  itemRequestFailed(id: string, error: FetchError) {
    this.draftState.itemFetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchError = error
  }

  filterUpdated(filter: ProductListRequestFilter) {
    this.draftState.filter = filter
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingUpdated(sorting: ProductListRequestSorting) {
    this.draftState.sorting = sorting
  }

  filterHasBeenReset() {
    this.draftState.filter = {}
  }

  sortingHasBeenReset() {
    this.draftState.sorting = {}
  }

  listRequestedNext(page: number) {
    this.draftState.page = page
    this.draftState.listFetchNextProgress = Progress.WORK
  }

  listRequestNextSucceed(list: Product[], total: number) {
    this.draftState.listFetchNextProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.items = { ...this.draftState.items, ...arrToDict(list) }
    this.draftState.ids = [...new Set([...this.draftState.ids, ...getIds(list)])]
  }

  listRequestNextFailed(error: FetchError) {
    this.draftState.listFetchNextProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  // eslint-disable-next-line immer-reducer/no-optional-or-default-value-params
  discountsRequested(_id: string, _qty: number, _params: Partial<CartItem> = {}) {
    this.draftState.discountsFetchProgress = Progress.WORK
    this.draftState.discountsFetchError = null
  }

  discountsRequestSucceed(dto: CalculateDiscountResponse) {
    this.draftState.discountsFetchProgress = Progress.SUCCESS
    this.draftState.discounts = dto.discounts
  }

  discountsRequestFailed(error: FetchError) {
    this.draftState.discountsFetchProgress = Progress.ERROR
    this.draftState.discountsFetchError = error
  }

  exclusiveItemRequested(_id: string) {
    this.draftState.fetchExclusiveItemProgress = Progress.WORK
    this.draftState.fetchExclusiveItemError = null
  }
  exclusiveItemRequestSucceed() {
    this.draftState.fetchExclusiveItemProgress = Progress.SUCCESS
    this.draftState.fetchExclusiveItemError = null
  }
  exclusiveItemRequestFailed(error: FetchError) {
    this.draftState.fetchExclusiveItemProgress = Progress.ERROR
    this.draftState.fetchExclusiveItemError = error
  }

  giftListRequested(filter: ProductListRequestFilter) {
    this.draftState.giftFilter = filter
    this.draftState.giftListFetchProgress = Progress.WORK
    this.draftState.giftListFetchError = null
  }
  giftListRequestSucceed(list: Product[]) {
    this.draftState.giftListFetchProgress = Progress.SUCCESS
    this.draftState.gifts = arrToDict(list)
  }
  giftListRequestFailed(error: FetchError) {
    this.draftState.giftListFetchProgress = Progress.ERROR
    this.draftState.giftListFetchError = error
  }

  customProductListRequested(_params: {
    filter: ProductListRequestFilter
    sorting?: ProductListRequestSorting
    page?: number
    pageSize?: number
    isExclusive?: boolean
  }) {
    this.draftState.customProductListFetchProgress = Progress.WORK
    this.draftState.customProductListFetchError = null
  }

  customProductListRequestSucceed(list: Product[]) {
    this.draftState.customProductListFetchProgress = Progress.SUCCESS
    this.draftState.customProductList = arrToDict(list)
  }

  customProductListListRequestFailed(error: FetchError) {
    this.draftState.customProductListFetchProgress = Progress.ERROR
    this.draftState.customProductListFetchError = error
  }
}

export const ProductActions = createActionCreators(ProductReducer)
export default ProductActions
export const reducer = createReducerFunction(ProductReducer, initialState)
